import { useState, useMemo, useCallback } from 'react';
import { SearchIcon, CloseIcon } from '@/components/icons';
import { usePaginatedWebPages } from '@/lib/api/hooks/web-pages';
import { DocumentIcon } from '@/assets/icons';
import { Button, Loader, TanStackTable, type ColumnDef, Pagination } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import formatDate from '@/utils/helpers/formatDate';
import { titleCase } from '@/utils/helpers/textTransform';
import { PaginatedWebPage } from '@/lib/api/types';

const PAGE_SIZE = 20;
const DEFAULT_LANGUAGE = 'EN';
const DEFAULT_STATUS = 'Published';

const normalizePageName = (pageName?: string | null) =>
    (pageName ?? '').toLowerCase().trim().replace(/\s+/g, ' ');

const toPageSlug = (pageName: string) => {
    let slug = "";
    switch(pageName.toLowerCase().trim()) {
        case "who we are":
            slug = "/about-us/who-we-are";
            break;
            case "our history":
                slug = "/about-us/history";
                break;
                case "leadership":
                    slug = "/about-us/leadership";
                    break;
                    case "legal foundations":
                        slug = "/about-us/legal-foundations";
                        break;
                        case "member countries":
                            slug = "/about-us/member-countries";
                            break;
            case "strategic pillars":
                slug = "/programmes-and-initiatives/strategic-pillars";
                break;
                case "programmes":
                    slug = "/programmes-and-initiatives/programmes";
                    break;
                    case "procurement":
                        slug = "/opportunities/procurement";
                        break;
                        case "careers":
                            slug = "/opportunities/careers";
                            break;
                            case "press release":
                                slug = "/media/press-release";
                                break;
                                case "home":
                                    slug = "/";
                                    break;
                                    case "events":
                                        slug = "/events";
                                        break;
                                        case "resources":
                                            slug = "/resources";
                                            break;
                                            case "contact us":
                                                slug = "/contact-us";
                                                break;
            default:
                slug = `/${pageName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
    }
    return slug;
}

const pageOrder: Record<string, number> = {
    // Header navItems order
    'home': 1,
    'who we are': 2,
    'our history': 3,
    'history': 3,
    'leadership': 4,
    'legal foundation': 5,
    'legal foundations': 5,
    'members countries': 6,
    'member countries': 6,
    'members country': 6,
    'strategic pillars': 7,
    'programmes': 8,
    'procurement': 9,
    'procurement docs': 9,
    'careers': 10,
    'jobs': 10,
    'press release': 11,
    'photo gallery': 12,
    'videos': 13,
    'events': 14,
    'resources': 15,
};

const WebPagesTable = () => {

    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const { data, isFetching, isError, refetch } = usePaginatedWebPages({ page: currentPage, size: PAGE_SIZE });

    const isInitialLoad = !data && isFetching;

    const paginationData = data?.data;
    const allWebPages = useMemo(() => paginationData?.data || [], [paginationData?.data]);
    const totalPages = paginationData?.totalPage || 1;

    const orderedWebPages = useMemo(() => {
        return [...allWebPages].sort((a, b) => {
            const aName = normalizePageName(a.websitePageName);
            const bName = normalizePageName(b.websitePageName);

            const aOrder = pageOrder[aName] ?? Number.MAX_SAFE_INTEGER;
            const bOrder = pageOrder[bName] ?? Number.MAX_SAFE_INTEGER;

            if (aOrder !== bOrder) return aOrder - bOrder;
            return aName.localeCompare(bName);
        });
    }, [allWebPages]);

    const webPagesData = useMemo(() => {
        if (!searchQuery.trim()) {
            return orderedWebPages;
        }

        const query = searchQuery.toLowerCase().trim();
        return orderedWebPages.filter((page: PaginatedWebPage) =>
            page.websitePageName?.toLowerCase().includes(query) ||
            page.updatedByName?.toLowerCase().includes(query)
        );
    }, [orderedWebPages, searchQuery]);

    const hasSearchQuery = !!searchQuery && searchQuery.trim().length > 0;

    const handleRowClick = useCallback((websitePageId: string) => {
        navigate(`web-page/${websitePageId}`);
    }, [navigate]);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Get initials from name for avatar
    const getInitials = (name: string | null): string => {
        if (!name) return '?';
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const columns = useMemo<ColumnDef<PaginatedWebPage, unknown>[]>(() => [
        {
            id: 'pages',
            header: 'Page Name',
            cell: ({ row }) => {
                const page = row.original;
                return (
                    <div className="flex items-center gap-3">
                        <DocumentIcon className="shrink-0" stroke="#681628" />
                        <div>
                            <div className="text-sm font-medium text-gray-900">{page.websitePageName}</div>
                            <div className="text-sm text-gray-500">{toPageSlug(page.websitePageName)}</div>
                        </div>
                    </div>
                );
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: 'px-6 py-4 whitespace-nowrap',
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: () => (
                <span className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-sm font-medium text-emerald-700">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    {DEFAULT_STATUS}
                </span>
            ),
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: 'px-6 py-4 whitespace-nowrap',
            },
        },
        {
            id: 'languages',
            header: 'Languages',
            cell: () => (
                <span className="inline-flex min-w-11 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-sm font-semibold text-emerald-700">
                    {DEFAULT_LANGUAGE}
                </span>
            ),
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: 'px-6 py-4 whitespace-nowrap',
            },
        },
        {
            id: 'lastUpdate',
            header: 'Last Updated',
            cell: ({ row }) => {
                const page = row.original;
                return (
                    <span className="text-sm text-gray-500">
                        {page?.updatedAt ? formatDate(page.updatedAt, 'DD/MM/YYYY hh:mm A') : ''}
                    </span>
                );
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: 'px-6 py-4 whitespace-nowrap',
            },
        },
        {
            id: 'updatedBy',
            header: 'Updated By',
            cell: ({ row }) => {
                const page = row.original;
                return page.updatedByName ? (
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                            <span className="text-white text-xs font-medium">{getInitials(page.updatedByName)}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{titleCase(page.updatedByName)}</span>
                    </div>
                ) : (
                    <span className="text-sm text-gray-400">-</span>
                );
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: 'px-6 py-4 whitespace-nowrap',
            },
        }
    ], []);

    if (isInitialLoad) {
        return <Loader size="md" variant="wine" message="Loading website pages..." />;
    }

    if (isError) {
        return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
            <p>Error loading website pages.</p>
            <Button onClick={() => refetch()} className="">Retry</Button>
        </div>;
    }

  return (
       <div className="bg-white border border-gray-200 rounded-xl shadow-sm relative">

                {isFetching && !isInitialLoad && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 z-10">
                        <div className="h-full bg-[#681628] animate-pulse" style={{ width: '30%' }}></div>
                    </div>
                )}
                <div className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-4">
                        <div className="relative flex-1 max-w-xs">
                            <label htmlFor="search-input" className="sr-only">Search</label>
                            <input
                                type="text"
                                id="search-input"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#681628] focus:border-transparent bg-[#FAFAFA]"
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <SearchIcon className="w-4 h-4 text-gray-400" />
                            </div>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                    type="button"
                                >
                                    <CloseIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {hasSearchQuery && (
                            <div className="text-sm text-gray-500">
                                {webPagesData.length} result{webPagesData.length !== 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    {webPagesData.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="inline-block bg-gray-200/20 p-4 rounded-full">
                                <DocumentIcon className="w-10 h-10 text-gray-400" stroke="#9CA3AF" />
                            </div>
                            <h3 className="mt-4 font-semibold text-gray-800 text-lg">No Pages</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {hasSearchQuery
                                    ? `No pages found matching "${searchQuery}"`
                                    : 'No website pages available'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <TanStackTable
                                data={webPagesData}
                                columns={columns}
                                getRowId={(row) => row.websitePageId}
                                onRowClick={(row) => handleRowClick(row.websitePageId)}
                                rowClassName="hover:bg-gray-50"
                            />
                        </div>
                    )}

                    {/* Pagination Controls */}
                    {totalPages > 1 && !hasSearchQuery && (
                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}

                </div>
            </div>
  )
}

export default WebPagesTable